import { CountryCode } from '@crehana/ts-types';

import { METHODS } from 'Jsx/CheckoutV4/common/constants';
import { CreditCardFormFields, PaymentMethodType } from 'Jsx/CheckoutV4/types';

import { UserData } from '../types';

export interface FormFieldsDisplay {
  email: boolean;
  document: boolean;
  cardHolder: boolean;
  cityAndState: boolean;
  address: boolean;
  phone: boolean;
  zipCode: boolean;
}

const getPaymentMethodFormFields = (
  paymentMethod: PaymentMethodType,
  countryPrefix: CountryCode,
): FormFieldsDisplay => {
  switch (paymentMethod.title || '') {
    case METHODS.STRIPE:
    case METHODS.STRIPE_MEXICO:
      return {
        email: true,
        document: countryPrefix === 'co',
        cardHolder: false,
        cityAndState: false,
        address: false,
        phone: false,
        zipCode: false,
      };
    case METHODS.MERCADOPAGO_CARD_MEXICO:
      return {
        email: true,
        /*
          We are setting this document field to FALSE to do not request for Mexican users
          document (CURP, this is a 20 lenght document and most of the mexican users
          doesn't remeber it)
        */
        document: false,
        cardHolder: true,
        cityAndState: false,
        address: false,
        phone: false,
        zipCode: false,
      };
    case METHODS.DLOCAL_AR:
    case METHODS.DLOCAL_PERU:
    case METHODS.DLOCAL_MEXICO:
    case METHODS.DLOCAL_COLOMBIA:
      return {
        email: true,
        document: true,
        cardHolder: true,
        cityAndState: false,
        address: false,
        phone: false,
        zipCode: false,
      };
    case METHODS.EBANX_CARD:
      return {
        email: true,
        document: true,
        cardHolder: true,
        cityAndState: true,
        address: true,
        phone: true,
        zipCode: true,
      };
    case METHODS.EBANX_CARD_AR:
      return {
        email: true,
        document: true,
        cardHolder: true,
        cityAndState: true,
        address: true,
        phone: true,
        zipCode: true,
      };
    case METHODS.PAYU_PERU:
      return {
        email: true,
        document: true,
        cardHolder: true,
        cityAndState: false,
        address: false,
        phone: true,
        zipCode: false,
      };
    default:
      return {
        cardHolder: false,
        email: true,
        address: false,
        zipCode: false,
        phone: false,
        document: false,
        cityAndState: false,
      };
  }
};

// This method is used to get the form fields that should be displayed in the CreditCard form.
// Because we are using a fallback logic, we need to have all the posible fields that should be displayed
// so that if the fallback is used, the user dont have to fill new fields, making it akward for him.

export function getUnionOfPaymentMethodsInputFields(
  paymentMethods: PaymentMethodType[] = [],
  countryPrefix: CountryCode,
): FormFieldsDisplay {
  let formFields: FormFieldsDisplay = {
    cardHolder: false,
    email: false,
    address: false,
    zipCode: false,
    phone: false,
    document: false,
    cityAndState: false,
  };

  paymentMethods.forEach(paymentMethod => {
    const paymentMethodsFormField = getPaymentMethodFormFields(
      paymentMethod,
      countryPrefix,
    );

    formFields = {
      cardHolder: formFields.cardHolder || paymentMethodsFormField.cardHolder,
      email: formFields.email || paymentMethodsFormField.email,
      address: formFields.address || paymentMethodsFormField.address,
      zipCode: formFields.zipCode || paymentMethodsFormField.zipCode,
      phone: formFields.phone || paymentMethodsFormField.phone,
      document: formFields.document || paymentMethodsFormField.document,
      cityAndState:
        formFields.cityAndState || paymentMethodsFormField.cityAndState,
    };
  });

  return formFields;
}

export const areUserFieldsCompletedAndValid = ({
  userData,
  formFields,
}: {
  userData: UserData;
  formFields: CreditCardFormFields;
}): boolean => {
  const isAddressCompleted = formFields.address
    ? userData.address?.isValid || false
    : true;
  const areCityAndStateCompleted = formFields.cityAndState
    ? userData.cityAndState?.isValid || false
    : true;
  const isDocumentCompleted = formFields.document
    ? userData.identityDocument?.isValid || false
    : true;
  const isPhoneCompleted = formFields.phone
    ? userData.phone?.isValid || false
    : true;
  const isZipCodeCompleted = formFields.zipCode
    ? userData.zipCode?.isValid || false
    : true;

  return (
    isAddressCompleted &&
    areCityAndStateCompleted &&
    isDocumentCompleted &&
    isPhoneCompleted &&
    isZipCodeCompleted
  );
};
